import * as React from 'react';

import type { RankCode } from '@/api/rank-admin-client';
import type {
  ExportJournalFiltersDto,
  JournalEntryDto,
  RecordExportBodyDto,
} from '@/api/rank-admin-client';
import {
  useAssignRank,
  useExportJournal,
  useFreezePanel,
  useListFreezes,
  useRankWorkingTable,
  useRecordExport,
  useRemoveRank,
} from '@/api/use-rank-admin';
import {
  useAnalysisPercentiles,
  useCampaignAnalysisMeta,
  useCampaignStacks,
  useTargetPriceSummary,
} from '@/api/use-campaign-analysis';
import { useCatalogProfiles } from '@/api/use-catalog-edition';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Panel } from '@/components/ui/panel';
import {
  DEFAULT_RANKING_SORT,
  DISTRIBUTION_UNCHANGED_NOTE,
  TOP_N_ALL,
  applyTopN,
  buildRankingRows,
  clampTopN,
  countDistinctSuppliers,
  currentFilterStats,
  deriveCouples,
  deriveTagCounters,
  distributionBounds,
  distributionForDisplayedRanking,
  effectiveCountByLevel,
  filterRankingRows,
  groupDistribution,
  kAnonymityFloor,
  profileOptions as deriveProfileOptions,
  sortRankingRows,
  sousFamilleByProfile as deriveSousFamilleByProfile,
  sousFamilleOptions as deriveSousFamilleOptions,
  supplierOptions as deriveSupplierOptions,
  topNSubtitle,
  type RankingSort,
  type RankingSortColumn,
  type SortDirection,
  type TopNSetting,
} from '@/lib/analysis-fusion';
import {
  DISTRIBUTION_JOURNAL_KEYS,
  RANKING_JOURNAL_KEYS,
  analysisExportFileName,
  buildAnalysisXlsx,
  buildDistributionCsv,
  buildRankingCsv,
} from '@/lib/benchmark-export';
import { RANK_PRESENTATION } from '@/lib/rank-labels';
import {
  TARGET_PRICE_SUMMARY_EXPORT_COLUMNS,
  buildTargetPriceSummaryCsv,
  targetPriceSummaryExportFileName,
} from '@/lib/target-price-summary-export';
import { AnalysisDistributionCard } from '@/routes/admin/campaigns/analysis-distribution-card';
import { AnalysisRankingCard } from '@/routes/admin/campaigns/analysis-ranking-card';
import {
  AnalysisCurrentFilterPanel,
  AnalysisExportPanel,
  AnalysisFiltersPanel,
  AnalysisPanelActions,
  type AnalysisExportSelection,
} from '@/routes/admin/campaigns/analysis-sidebar';
import { FrozenVersionsPanel } from '@/routes/admin/campaigns/rank-frozen-versions-panel';
import { TargetPriceSummaryPanel } from '@/routes/admin/campaigns/target-price-summary-panel';
import { OperationalViewComparisonPanel } from '@/routes/admin/operational-view/operational-view-comparison-panel';

// =============================================================================
// campaign-analysis-screen.tsx — Page ADMIN « Analyse » FUSIONNÉE (lot U1, ADR-FUSION-ANALYSE-RANG-01).
// =============================================================================
// Une SEULE page remplace la page d'analyse ET la vue « Référencement / Rang » (route + onglet
// SUPPRIMÉS). Décisions GATE_HUMAN John du 2026-09-17 (verbatim en tête de
// `tasks/spec-fusion-analyse-rang-20260917.md`), DBU v2.9 §2.6, UX v1.3 §8.5.
//
// DISPOSITION (maquette 2026-09-17, amendée U1-bis) : bloc HAUT à deux colonnes = Distribution
// anonymisée + colonne latérale FIXE 340 px (Filtres, Filtre courant, Export, Panel) ; PUIS le Ranking
// nominatif EN PLEINE LARGEUR ; pied de page = tableau définitif « Prix cible et médianes », historique
// des versions figées, journal des exports.
//
// CE QUE LA PAGE COMPOSE (aucune règle métier : l'autorité reste l'API/worker) :
//   1. `useAnalysisPercentiles` — distribution ANONYMISÉE (7 bornes winsorisées par couple, gate
//      K-anon serveur) ; groupée par profil, jamais nominative ;
//   2. `useRankWorkingTable` — ranking NOMINATIF au grain trinôme, enrichi par le lot A1
//      (`responseC1Eur`, `responseC2Eur`, `coupleEffectiveCount`, `targetPriceEur`,
//      `ecartTargetPricePct`) ; le tag, le TOP N, le tri et les chips s'y appliquent ;
//   3. `useCampaignStacks` — référentiel du filtre « Sous-famille » (A1 : `profiles[].sousFamille`) ;
//   4. `useCatalogProfiles` — LIBELLÉ de profil du catalogue FIGÉ (jointure d'affichage front, parité
//      de la vue opérationnelle C1-bis) ; absent ⇒ seul le code est rendu ;
//   5. `useListFreezes` / `useExportJournal` — historique des figements et journal d'audit.
//
// DÉPLACEMENT DU 2026-09-22 (décision GATE_HUMAN, verbatim : « il faut déplacer seulement la
// fonctionnalité négociation sur la page […]/operational-view/fournisseurs ») : cet écran NE PORTE
// PLUS l'ouverture de négociation. `useNegotiationAuthState` / `useReopenNegotiation` en ont donc
// disparu, avec la colonne ⇄ et son compteur. La page d'analyse redevient une page de LECTURE et de
// TAG ; renégocier appartient à la vue opérationnelle Fournisseurs, où l'admin voit, par profil et
// par fournisseur, le TJM qui n'est pas compétitif.
//
// ÉTAT DE VUE : profil, sous-famille, fournisseur, TOP N, chip de tag et tri vivent dans l'URL
// (RULE_REF_006 — aucun localStorage) ; seules la sélection en masse, les cases d'export et
// l'ouverture du panneau de comparaison sont de l'état de session in-memory.
//
// ÉTAT MACHINE (DBU §2.6) : analyse `EnAttente|EnTraitement` ⇒ écran de progression role="status",
// aucun tableau (jamais de table vide trompeuse).

/** MIME du classeur XLSX (OOXML). */
const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Liste vide PARTAGÉE : « aucun filtre posé ». Référence stable, pour ne pas invalider les memos. */
const EMPTY_FILTER: readonly string[] = [];

/**
 * Patch d'état d'URL émis par la page (le routeur le fusionne dans les search params).
 *
 * LOT F1 (GATE_HUMAN 2026-09-21) : `profil`, `sousFamille` et `fournisseur` portent une LISTE. Une
 * liste VIDE vaut « aucun filtre » et fait disparaître la clé de l'URL (aucune valeur sentinelle).
 */
export interface AnalysisSearchPatch {
  readonly profil?: readonly string[] | undefined;
  readonly sousFamille?: readonly string[] | undefined;
  readonly fournisseur?: readonly string[] | undefined;
  readonly topN?: number | undefined;
  readonly tag?: RankCode | undefined;
  readonly tri?: RankingSortColumn | undefined;
  readonly dir?: SortDirection | undefined;
}

export interface CampaignAnalysisScreenProps {
  readonly campaignId: string;
  /** Filtre profil, multi-valeurs (état d'URL ; absent ou vide = tous). */
  readonly profileCodes?: readonly string[] | undefined;
  /** Filtre sous-famille, multi-valeurs (état d'URL ; absent ou vide = toutes). */
  readonly sousFamilles?: readonly string[] | undefined;
  /** Filtre fournisseur, aux SIRET, multi-valeurs (état d'URL ; absent ou vide = tous). */
  readonly supplierSirets?: readonly string[] | undefined;
  /** TOP N par couple (état d'URL ; `undefined` = réglage INACTIF, toutes les lignes — U1-bis). */
  readonly topN?: number | undefined;
  /** Chip de tag active (état d'URL ; `undefined` = tous les rangs). */
  readonly tag?: RankCode | undefined;
  /** Colonne de tri (état d'URL ; `undefined` = tri par défaut profil + TJM C2). */
  readonly sortColumn?: RankingSortColumn | undefined;
  /** Sens de tri (état d'URL ; `undefined` = ascendant). */
  readonly sortDirection?: SortDirection | undefined;
  /** Écrit l'état d'URL (merge sur les search params existants). */
  readonly onChangeSearch: (patch: AnalysisSearchPatch) => void;
  /** Réinitialise TOUS les filtres, le TOP N et le tri (retour à l'état initial). */
  readonly onResetSearch: () => void;
}

export function CampaignAnalysisScreen({
  campaignId,
  profileCodes,
  sousFamilles,
  supplierSirets,
  topN,
  tag,
  sortColumn,
  sortDirection,
  onChangeSearch,
  onResetSearch,
}: CampaignAnalysisScreenProps): React.JSX.Element {
  const effectiveTopN = clampTopN(topN);
  // Listes NORMALISÉES une fois pour toutes : « absent » et « vide » sont le même état (aucun filtre),
  // et la référence est stable tant que l'URL ne change pas (les memos en aval en dépendent).
  const profiles = React.useMemo(() => profileCodes ?? EMPTY_FILTER, [profileCodes]);
  const sousFamilleFilter = React.useMemo(() => sousFamilles ?? EMPTY_FILTER, [sousFamilles]);
  const suppliers = React.useMemo(() => supplierSirets ?? EMPTY_FILTER, [supplierSirets]);
  // Mémoïsé : un nouvel objet de tri à chaque render invaliderait le memo du tableau trié (coût
  // inutile sur un ranking de plusieurs centaines de lignes — budget d'interaction ENF-01).
  const sort = React.useMemo<RankingSort>(
    () =>
      sortColumn === undefined
        ? DEFAULT_RANKING_SORT
        : { column: sortColumn, direction: sortDirection ?? 'asc' },
    [sortColumn, sortDirection],
  );

  // --- Lectures -----------------------------------------------------------------------------------
  const metaQuery = useCampaignAnalysisMeta(campaignId);
  const analysisState = metaQuery.data?.state;
  const inProgress = analysisState === 'EnAttente' || analysisState === 'EnTraitement';

  // Filtre SERVEUR de la distribution : le profil seulement (la sous-famille est une jointure front).
  // Le contrat `/analysis/percentiles` ne porte QU'UN code de profil : le filtre serveur n'est donc
  // posé que lorsqu'UN SEUL profil est coché (comportement strictement identique à la version à choix
  // unique). Au-delà, la page lit TOUT le périmètre et restreint FRONT-SIDE (cf. `distributionRows`) :
  // même ensemble de lignes, même gate K-anonymity serveur, aucune évolution d'API requise.
  const percentileFilters = React.useMemo(
    () => (profiles.length === 1 ? { profileCode: profiles[0] as string } : {}),
    [profiles],
  );
  const percentilesQuery = useAnalysisPercentiles(campaignId, percentileFilters);
  const stacksQuery = useCampaignStacks(campaignId);
  const tableQuery = useRankWorkingTable(campaignId);
  const summaryQuery = useTargetPriceSummary(campaignId, !inProgress);
  const journalQuery = useExportJournal(campaignId);
  const freezesQuery = useListFreezes(campaignId);

  // Libellés de profil : catalogue FIGÉ de l'analyse (jointure d'AFFICHAGE, aucune règle métier).
  const catalogVersionId = metaQuery.data?.catalogVersionId ?? '';
  const catalogProfilesQuery = useCatalogProfiles(
    catalogVersionId,
    catalogVersionId !== '',
  );

  // --- Commandes ----------------------------------------------------------------------------------
  const assignRank = useAssignRank(campaignId);
  const removeRank = useRemoveRank(campaignId);
  const freezePanel = useFreezePanel(campaignId);
  const recordExport = useRecordExport(campaignId);

  // --- État de session (in-memory ; jamais localStorage — RULE_REF_006) ---------------------------
  const [selected, setSelected] = React.useState<ReadonlySet<string>>(() => new Set());
  const [freezeOpen, setFreezeOpen] = React.useState(false);
  const [comparisonExpanded, setComparisonExpanded] = React.useState(false);

  // --- Dérivations ---------------------------------------------------------------------------------
  const kFloor = kAnonymityFloor(metaQuery.data?.kAnonymityThreshold);
  const allRankRows = React.useMemo(() => tableQuery.data?.rows ?? [], [tableQuery.data?.rows]);
  const stackProfiles = React.useMemo(
    () => stacksQuery.data?.profiles ?? [],
    [stacksQuery.data?.profiles],
  );
  const sousFamilleByProfile = React.useMemo(
    () => deriveSousFamilleByProfile(stackProfiles),
    [stackProfiles],
  );
  const profileLabels = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const profile of catalogProfilesQuery.data ?? []) {
      map.set(profile.profileCode, profile.libelle);
    }
    return map;
  }, [catalogProfilesQuery.data]);

  // Distribution : profil (serveur) + sous-famille (front). NI le fournisseur, NI le tag, NI le TOP N
  // ne la touchent — elle reste l'agrégat de la population complète du couple (décision 5).
  const distributionRows = React.useMemo(() => {
    let rows = percentilesQuery.data?.rows ?? [];
    // Profil : filtre SERVEUR quand un seul code est coché, sinon restriction front-side (cf. supra).
    if (profiles.length > 1) {
      rows = rows.filter((r) => profiles.includes(r.profileCode));
    }
    if (sousFamilleFilter.length > 0) {
      rows = rows.filter((r) => {
        const value = sousFamilleByProfile.get(r.profileCode);
        return value !== undefined && sousFamilleFilter.includes(value);
      });
    }
    return rows;
  }, [percentilesQuery.data?.rows, profiles, sousFamilleFilter, sousFamilleByProfile]);

  const viewRows = React.useMemo(
    () => buildRankingRows(allRankRows, kFloor, sousFamilleByProfile),
    [allRankRows, kFloor, sousFamilleByProfile],
  );
  const filteredRows = React.useMemo(
    () =>
      filterRankingRows(viewRows, {
        profileCodes: profiles,
        sousFamilles: sousFamilleFilter,
        supplierSirets: suppliers,
        ...(tag !== undefined ? { tag } : {}),
      }),
    [viewRows, profiles, sousFamilleFilter, suppliers, tag],
  );
  const rankingRows = React.useMemo(
    () => sortRankingRows(applyTopN(filteredRows, effectiveTopN), sort),
    [filteredRows, effectiveTopN, sort],
  );
  const couples = React.useMemo(() => deriveCouples(rankingRows), [rankingRows]);
  const couplesByKey = React.useMemo(
    () => new Map(couples.map((c) => [c.key, c])),
    [couples],
  );
  const counters = React.useMemo(() => deriveTagCounters(rankingRows), [rankingRows]);
  const distributionGroups = React.useMemo(
    () =>
      groupDistribution(
        distributionRows,
        effectiveCountByLevel(allRankRows),
        profileLabels,
      ),
    [distributionRows, allRankRows, profileLabels],
  );
  // FILTRE COURANT (retour GATE_HUMAN 2026-09-18) : les 3 chiffres ET les bornes MIN…MAX portent sur
  // les couples RÉELLEMENT AFFICHÉS par le ranking — chips Tag et réglage TOP N compris. La card
  // Distribution, elle, garde son propre périmètre (agrégat de la population complète du couple).
  const displayedDistributionRows = React.useMemo(
    () => distributionForDisplayedRanking(distributionRows, rankingRows),
    [distributionRows, rankingRows],
  );
  const stats = React.useMemo(
    () => currentFilterStats(displayedDistributionRows, rankingRows),
    [displayedDistributionRows, rankingRows],
  );
  const bounds = React.useMemo(
    () => distributionBounds(displayedDistributionRows),
    [displayedDistributionRows],
  );
  /**
   * Libellés des filtres actifs, écrits en clair sous les chiffres du panneau « Filtre courant ».
   * MULTI-VALEURS (lot F1) : les valeurs d'un même filtre sont énumérées, séparées par des virgules
   * (« Fournisseur ACME SAS, BETA SARL ») — le périmètre reste LISIBLE, jamais réduit à un compte.
   */
  const activeFilterLabels = React.useMemo((): readonly string[] => {
    const labels: string[] = [];
    if (profiles.length > 0) labels.push(`Profil ${profiles.join(', ')}`);
    if (sousFamilleFilter.length > 0) {
      labels.push(`Sous-famille ${sousFamilleFilter.join(', ')}`);
    }
    if (suppliers.length > 0) {
      const named = suppliers.map((siret) => {
        const raisonSociale = allRankRows.find(
          (r) => r.supplierSiret === siret,
        )?.raisonSociale;
        return raisonSociale === undefined || raisonSociale === '' ? siret : raisonSociale;
      });
      labels.push(`Fournisseur ${named.join(', ')}`);
    }
    if (tag !== undefined) labels.push(`Tag ${RANK_PRESENTATION[tag].label}`);
    if (effectiveTopN !== TOP_N_ALL) labels.push(topNSubtitle(effectiveTopN));
    return labels;
  }, [profiles, sousFamilleFilter, suppliers, tag, effectiveTopN, allRankRows]);
  /** Compteurs PROPRES à la card Distribution (population complète du couple, décision 5). */
  const distributionCoupleCount = distributionRows.length;
  const distributionExploitableCount = distributionRows.filter((r) => !r.suppressed).length;

  // --- Handlers d'état d'URL ----------------------------------------------------------------------
  const onSort = React.useCallback(
    (column: RankingSortColumn): void => {
      const nextDirection: SortDirection =
        sort.column === column && sort.direction === 'asc' ? 'desc' : 'asc';
      onChangeSearch({ tri: column, dir: nextDirection });
    },
    [sort, onChangeSearch],
  );
  const onToggleTag = React.useCallback(
    (next: RankCode): void => {
      onChangeSearch({ tag: tag === next ? undefined : next });
    },
    [tag, onChangeSearch],
  );

  // --- Commandes de tag ---------------------------------------------------------------------------
  const applyRank = React.useCallback(
    (coupleKey: string, rank: RankCode): void => {
      const couple = couplesByKey.get(coupleKey);
      if (couple === undefined) return;
      const target = {
        supplierSiret: couple.supplierSiret,
        profileCode: couple.profileCode,
      };
      if (rank === 'NonReferenced') {
        removeRank.mutate(target);
        return;
      }
      assignRank.mutate({ ...target, rank });
    },
    [couplesByKey, assignRank, removeRank],
  );
  const onApplyMassRank = React.useCallback(
    (rank: 'A' | 'B' | 'PROSPECT'): void => {
      for (const key of selected) {
        const couple = couplesByKey.get(key);
        if (couple !== undefined) {
          assignRank.mutate({
            supplierSiret: couple.supplierSiret,
            profileCode: couple.profileCode,
            rank,
          });
        }
      }
      setSelected(new Set());
    },
    [selected, couplesByKey, assignRank],
  );
  const onRemoveMassRank = React.useCallback((): void => {
    for (const key of selected) {
      const couple = couplesByKey.get(key);
      if (couple !== undefined) {
        removeRank.mutate({
          supplierSiret: couple.supplierSiret,
          profileCode: couple.profileCode,
        });
      }
    }
    setSelected(new Set());
  }, [selected, couplesByKey, removeRank]);
  /**
   * Case « tout sélectionner » de l'en-tête (retour GATE_HUMAN 2026-09-18) : la sélection devient
   * EXACTEMENT l'ensemble des binômes affichés — elle n'ajoute pas à une sélection devenue invisible
   * après un changement de filtre (l'attribution en masse ne porte jamais sur ce qu'on ne voit pas).
   */
  const onSelectAllDisplayed = React.useCallback((keys: readonly string[]): void => {
    setSelected(new Set(keys));
  }, []);
  const onToggleSelected = React.useCallback((key: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // --- Export (2 cases : XLSX = 1 classeur 2 feuilles ; CSV = 1 fichier par case) ------------------
  const journalFilters = React.useMemo((): ExportJournalFiltersDto => {
    // Le contrat d'audit ne porte qu'UN `profileCode`/`seniorityLevel` : la sous-famille, le
    // fournisseur et le TOP N ne sont pas journalisables sans évolution d'API (limite signalée).
    // Depuis le lot F1 le filtre profil est une LISTE : elle n'est journalisable que lorsqu'elle
    // tient dans le contrat, c'est-à-dire à UN seul code (au-delà, l'entrée reste sans filtre plutôt
    // que de déclarer un périmètre FAUX dans le journal d'audit).
    return profiles.length === 1 ? { profileCode: profiles[0] as string } : {};
  }, [profiles]);

  const onExport = React.useCallback(
    (format: 'csv' | 'xlsx', selection: AnalysisExportSelection): void => {
      if (!selection.distribution && !selection.ranking) return;
      const record = (body: RecordExportBodyDto): void => {
        recordExport.mutate(body);
      };
      if (format === 'xlsx') {
        const bytes = buildAnalysisXlsx({
          ...(selection.distribution ? { distribution: distributionRows } : {}),
          ...(selection.ranking ? { ranking: rankingRows } : {}),
        });
        triggerDownload(
          analysisExportFileName('classeur', 'xlsx'),
          new Blob([bytes], { type: XLSX_MIME }),
        );
        // UN fichier ⇒ UNE entrée de journal, portant les colonnes des DEUX feuilles (préfixées).
        record({
          format: 'xlsx',
          anonymized: !selection.ranking,
          selectedColumns: [
            ...(selection.distribution ? DISTRIBUTION_JOURNAL_KEYS : []),
            ...(selection.ranking ? RANKING_JOURNAL_KEYS : []),
          ],
          filters: journalFilters,
          rowCount:
            (selection.distribution ? distributionRows.length : 0) +
            (selection.ranking ? rankingRows.length : 0),
        });
        return;
      }
      // CSV : DEUX téléchargements successifs quand les deux cases sont cochées, et DEUX entrées de
      // journal (une par fichier) — décision GATE_HUMAN « procède avec a. », aucune archive zip.
      if (selection.distribution) {
        triggerDownload(
          analysisExportFileName('distribution', 'csv'),
          csvBlob(buildDistributionCsv(distributionRows)),
        );
        record({
          format: 'csv',
          anonymized: true,
          selectedColumns: DISTRIBUTION_JOURNAL_KEYS,
          filters: journalFilters,
          rowCount: distributionRows.length,
        });
      }
      if (selection.ranking) {
        triggerDownload(
          analysisExportFileName('ranking', 'csv'),
          csvBlob(buildRankingCsv(rankingRows)),
        );
        record({
          format: 'csv',
          anonymized: false,
          selectedColumns: RANKING_JOURNAL_KEYS,
          filters: journalFilters,
          rowCount: rankingRows.length,
        });
      }
    },
    [distributionRows, rankingRows, journalFilters, recordExport],
  );

  /** Export CSV DÉDIÉ du tableau définitif L9 (grain couple, incompatible avec les 2 cases). */
  const summaryRows = summaryQuery.data?.rows;
  const onExportSummaryCsv = React.useCallback((): void => {
    if (summaryRows === undefined) return;
    triggerDownload(
      targetPriceSummaryExportFileName(),
      csvBlob(buildTargetPriceSummaryCsv(summaryRows)),
    );
    recordExport.mutate({
      format: 'csv',
      anonymized: false,
      selectedColumns: TARGET_PRICE_SUMMARY_EXPORT_COLUMNS.map((c) => c.key),
      filters: {},
      rowCount: summaryRows.length,
    });
  }, [summaryRows, recordExport]);

  const journalEntries = journalQuery.data?.entries ?? [];
  const latestFreeze = freezesQuery.data?.[0];
  const hasActiveFilters =
    profiles.length > 0 ||
    sousFamilleFilter.length > 0 ||
    suppliers.length > 0 ||
    tag !== undefined ||
    effectiveTopN !== clampTopN(undefined) ||
    sortColumn !== undefined;

  const commandError = assignRank.error?.message ?? removeRank.error?.message;

  if (metaQuery.isPending) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Chargement de l’analyse…
      </p>
    );
  }
  if (metaQuery.isError) {
    return (
      <p role="alert" className="text-sm text-destructive">
        Erreur de chargement de l’analyse : {metaQuery.error.message}
      </p>
    );
  }
  if (inProgress) {
    return <ProgressPanel state={analysisState} />;
  }

  return (
    <div className="space-y-6">
      {/* Ligne de contexte : date de calcul de l'analyse (l'intitulé, l'état et le catalogue figé sont
          portés UNE FOIS par l'en-tête de zone, rendu par le layout de la zone campagne). */}
      <p className="text-xs text-muted-foreground">
        {metaQuery.data !== undefined ? (
          <>
            Analyse calculée le{' '}
            <time dateTime={metaQuery.data.computedAt} className="tabular-nums">
              {metaQuery.data.computedAt.slice(0, 10)}
            </time>{' '}
            · seuil de K-anonymity {String(kFloor)} répondants · {DISTRIBUTION_UNCHANGED_NOTE}
          </>
        ) : null}
      </p>

      {/* DISPOSITION (retour GATE_HUMAN 2026-09-19, verbatim : « La table Distribution doit être
          positionné comme celle du Ranking Nominatif, avec la colonne "filtres" qui se positionne à son
          côté sur la droite. La disposition actuelle devient illisible avec un vrai référentiel […] avec
          plus de 30 Profils (x4 niveaux de séniorité) ») : DEUX colonnes. La colonne principale EMPILE
          Distribution PUIS Ranking, à la MÊME largeur ; la colonne droite (340 px) les longe toutes les
          deux, donc les filtres restent atteignables même quand la Distribution fait 120 lignes.
          Les deux tableaux vivent dans la MÊME cellule de grille (empilés) : aucune ligne de grille ne
          peut alors se dilater à la hauteur de la colonne droite, ce qui rouvrirait la bande blanche
          entre eux. Basculement à `xl` (1280 px) ; en dessous tout s'empile, filtres AVANT les tableaux
          (`order-*`) : sur écran étroit, on n'a pas à dépasser deux tableaux pour atteindre un filtre. */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="order-1 min-w-0 space-y-6 xl:order-none xl:col-start-1 xl:row-start-1">
          <AnalysisDistributionCard
            groups={distributionGroups}
            coupleCount={distributionCoupleCount}
            exploitableCount={distributionExploitableCount}
            kFloor={kFloor}
            isPending={percentilesQuery.isPending}
            errorMessage={
              percentilesQuery.isError ? percentilesQuery.error.message : undefined
            }
            comparisonExpanded={comparisonExpanded}
            onToggleComparison={() => setComparisonExpanded((value) => !value)}
            comparisonPanelId="analysis-comparison-panel"
          />
          {/* Panneau de comparaison (replié par défaut, aucune lecture tant qu'il l'est). */}
          <OperationalViewComparisonPanel
            campaignId={campaignId}
            expanded={comparisonExpanded}
            panelId="analysis-comparison-panel"
          />

          {/* Le ranking porte 13 colonnes (Sous-famille incluse) : il occupe la colonne principale, à
              GAUCHE des indicateurs. Plus étroit que la page, son conteneur défile horizontalement au
              besoin ; les largeurs restent ajustables et mémorisées (U1-bis). */}
          <div className="min-w-0">
            <AnalysisRankingCard
              rows={rankingRows}
              totalRows={allRankRows.length}
              coupleCount={couples.length}
              supplierCount={countDistinctSuppliers(rankingRows)}
              topN={effectiveTopN}
              counters={counters}
              tag={tag}
              onToggleTag={onToggleTag}
              sort={sort}
              onSort={onSort}
              profileLabels={profileLabels}
              selected={selected}
              onToggleSelected={onToggleSelected}
              onSelectAllDisplayed={onSelectAllDisplayed}
              onClearSelection={() => setSelected(new Set())}
              onApplyMassRank={onApplyMassRank}
              onRemoveMassRank={onRemoveMassRank}
              onChangeRank={applyRank}
              tagPending={assignRank.isPending || removeRank.isPending}
              errorMessage={commandError}
              isPending={tableQuery.isPending}
              loadErrorMessage={tableQuery.isError ? tableQuery.error.message : undefined}
            />
          </div>
        </div>


        <aside
          aria-label="Filtres, synthèse et export du filtre courant"
          className="order-2 min-w-0 space-y-6 xl:order-none xl:col-start-2 xl:row-start-1 xl:self-start"
        >
          <AnalysisFiltersPanel
            profileCodes={profiles}
            profileOptions={deriveProfileOptions(allRankRows)}
            onChangeProfiles={(values) => onChangeSearch({ profil: values })}
            sousFamilles={sousFamilleFilter}
            sousFamilleOptions={deriveSousFamilleOptions(stackProfiles)}
            onChangeSousFamilles={(values) => onChangeSearch({ sousFamille: values })}
            supplierSirets={suppliers}
            supplierOptions={deriveSupplierOptions(allRankRows)}
            onChangeSuppliers={(values) => onChangeSearch({ fournisseur: values })}
            topN={effectiveTopN}
            // Réglage INACTIF ⇒ la clé `topN` DISPARAÎT de l'URL (aucun « ?topN=0 » trompeur).
            onChangeTopN={(value: TopNSetting) =>
              onChangeSearch({ topN: value === TOP_N_ALL ? undefined : value })
            }
            onReset={onResetSearch}
            hasActiveFilters={hasActiveFilters}
          />
          <AnalysisCurrentFilterPanel
            stats={stats}
            bounds={bounds}
            rankingRowCount={rankingRows.length}
            activeFilterLabels={activeFilterLabels}
          />
          <AnalysisExportPanel
            distributionCount={distributionRows.length}
            rankingCount={rankingRows.length}
            onExport={onExport}
            journalCount={journalEntries.length}
            journalLastAt={journalEntries[0]?.occurredAt}
          />
          <AnalysisPanelActions
            retainedCount={counters.A + counters.B + counters.PROSPECT}
            coupleCount={couples.length}
            latestFreeze={
              latestFreeze === undefined
                ? undefined
                : {
                    versionNumber: latestFreeze.versionNumber,
                    frozenAt: latestFreeze.frozenAt,
                    postPublication: latestFreeze.postPublication,
                  }
            }
            onRequestFreeze={() => setFreezeOpen(true)}
            freezePending={freezePanel.isPending}
            freezeRelayed={freezePanel.isSuccess && freezePanel.data.status === 'relayed'}
            freezeErrorMessage={freezePanel.error?.message}
          />
        </aside>
      </div>

      {/* Pied de page : tableau définitif L9, historique des versions figées, journal des exports. */}
      <TargetPriceSummaryPanel
        query={summaryQuery}
        onExportCsv={onExportSummaryCsv}
        exportDisabled={summaryRows === undefined || summaryRows.length === 0}
      />
      <FrozenVersionsPanel campaignId={campaignId} />
      <ExportJournalPanel
        entries={journalEntries}
        isPending={journalQuery.isPending}
        errorMessage={journalQuery.isError ? journalQuery.error.message : undefined}
      />

      {/* Confirmation FORTE du figement (décision 8) ; la vue reste ÉDITABLE après relais. */}
      <ConfirmDialog
        open={freezeOpen}
        title="Figer le panel de référencement ?"
        description="Une demande de figement sera transmise au moteur. Le panel courant (rangs A, B et Prospect) sera figé en une version horodatée, dont le livrable est produit au seul format XLSX. La vue reste modifiable : vous pourrez re-figer après ajustement, même après la publication Pulse (aucun effet sur le snapshot publié)."
        confirmLabel="Figer le panel"
        cancelLabel="Annuler"
        onConfirm={() => {
          setFreezeOpen(false);
          freezePanel.mutate();
        }}
        onCancel={() => setFreezeOpen(false)}
      />
    </div>
  );
}

/**
 * Déclenche le téléchargement d'un fichier via Blob + lien temporaire. Isolé pour rester TESTABLE
 * (mock de URL.createObjectURL en test). Aucune écriture disque persistante, aucune I/O réseau : le
 * fichier est produit LOCALEMENT, seule sa métadonnée est journalisée côté serveur.
 */
function triggerDownload(fileName: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Blob CSV avec BOM UTF-8 (accents lisibles par Excel FR), parité des autres exports de l'app. */
function csvBlob(csv: string): Blob {
  return new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
}

/** (ÉTAT MACHINE) Calcul en cours : progression, AUCUN rapport ni tableau (DBU §2.6). */
function ProgressPanel({
  state,
}: {
  readonly state: string | undefined;
}): React.JSX.Element {
  const label =
    state === 'EnAttente'
      ? 'Analyse en attente de traitement'
      : 'Analyse en cours de traitement';
  return (
    <Card>
      <CardHeader>
        <CardTitle>Traitement de l’analyse</CardTitle>
        <CardDescription>
          Le calcul consolidé (winsorization + percentiles) n’est pas encore disponible. La
          distribution et le ranking apparaîtront une fois l’analyse terminée.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <p role="status" className="flex items-center gap-2 text-muted-foreground">
          <span
            aria-hidden="true"
            className="h-3 w-3 animate-pulse rounded-full bg-muted-foreground"
          />
          <span>{label}…</span>
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * Journal des exports EN PIED DE PAGE (UX v1.3 §8.5) : liste unifiée export + figement, déjà triée
 * occurredAt DESC par l'API. Une ligne PAR FICHIER exporté (un export CSV à deux cases en produit deux).
 */
function ExportJournalPanel({
  entries,
  isPending,
  errorMessage,
}: {
  readonly entries: readonly JournalEntryDto[];
  readonly isPending: boolean;
  readonly errorMessage: string | undefined;
}): React.JSX.Element {
  return (
    <Panel
      headingId="analysis-export-journal-heading"
      title="Journal des exports"
      count={entries.length}
      aside={<span>une ligne par fichier exporté · figements inclus</span>}
    >
      {isPending ? (
        <p role="status" className="px-6 py-4 text-sm text-muted-foreground">
          Chargement du journal…
        </p>
      ) : errorMessage !== undefined ? (
        <p role="alert" className="px-6 py-4 text-sm text-destructive">
          Erreur de chargement du journal : {errorMessage}
        </p>
      ) : entries.length === 0 ? (
        <p role="status" className="px-6 py-4 text-sm text-muted-foreground">
          Aucun export ni figement journalisé pour le moment.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="ds-table w-full border-collapse text-sm">
            <caption className="sr-only">
              Journal des événements d’export et de figement du panel, du plus récent au plus
              ancien.
            </caption>
            <thead>
              <tr className="border-b border-border">
                <th scope="col" className="px-4 py-2 text-left">
                  Type
                </th>
                <th scope="col" className="px-4 py-2 text-left">
                  Date
                </th>
                <th scope="col" className="px-4 py-2 text-left">
                  Auteur
                </th>
                <th scope="col" className="px-4 py-2 text-left">
                  Détail
                </th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, index) => (
                <tr
                  key={`${entry.kind}:${entry.occurredAt}:${String(index)}`}
                  className="border-b border-border last:border-0"
                >
                  <th scope="row" className="px-4 py-2 text-left font-medium">
                    {entry.kind === 'export' ? 'Export' : 'Figement'}
                  </th>
                  <td className="px-4 py-2 tabular-nums">
                    <time dateTime={entry.occurredAt}>{entry.occurredAt.slice(0, 10)}</time>
                  </td>
                  <td className="px-4 py-2">{entry.actorRef}</td>
                  <td className="px-4 py-2">
                    {entry.kind === 'export'
                      ? formatExportDetail(entry)
                      : formatFreezeDetail(entry)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

/** Détail lisible d'une entrée d'export (format + destinataire + nombre de lignes). */
function formatExportDetail(entry: JournalEntryDto): string {
  const parts: string[] = [];
  if (entry.format !== null) {
    parts.push(entry.format.toUpperCase());
  }
  parts.push(entry.anonymized === true ? 'anonymisé' : 'nominatif');
  if (entry.rowCount !== null) {
    parts.push(`${String(entry.rowCount)} ligne(s)`);
  }
  return parts.join(' · ');
}

/** Détail lisible d'une entrée de figement (version + taille du panel). */
function formatFreezeDetail(entry: JournalEntryDto): string {
  const parts: string[] = [];
  if (entry.versionNumber !== null) {
    parts.push(`version ${String(entry.versionNumber)}`);
  }
  if (entry.panelSize !== null) {
    parts.push(`panel ${String(entry.panelSize)}`);
  }
  return parts.length === 0 ? '—' : parts.join(' · ');
}
